import {
  type SyncLogStatusFilter,
  useGoogleConnection,
  useGoogleDisconnect,
  useGoogleSyncLog,
} from "@/hooks/useGoogleConnection";
import { formatRelativeTime } from "@/lib/relativeTime";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { useState } from "react";

// ─── Sub-components ───────────────────────────────────────────────────────────

function ConnectionStatusBadge({ status }: { status: "active" | "expired" | null }) {
  if (status === "active") {
    return <span className="text-success font-medium">Connected</span>;
  }
  if (status === "expired") {
    return <span className="text-warning font-medium">Expired — reconnect needed</span>;
  }
  return <span className="text-muted-foreground">Not connected</span>;
}

function ConnectionActions({
  tenantId,
  status,
  connected,
}: {
  tenantId: string;
  status: "active" | "expired" | null;
  connected: boolean;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const disconnect = useGoogleDisconnect();

  const handleConnect = () => {
    window.location.href = `/portal/admin/google/connect?tenantId=${tenantId}`;
  };

  const handleDisconnectConfirm = () => {
    disconnect.mutate(tenantId);
    setConfirmOpen(false);
  };

  return (
    <div className="flex items-center gap-2 mt-3">
      {(!connected || status === "expired") && (
        <Button size="sm" onClick={handleConnect}>
          {status === "expired" ? "Reconnect" : "Connect Google"}
        </Button>
      )}
      {connected && status === "active" && (
        <>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive border-border hover:bg-destructive/10"
            onClick={() => setConfirmOpen(true)}
          >
            Disconnect
          </Button>
          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Disconnect Google Account</AlertDialogTitle>
                <AlertDialogDescription>
                  This will remove the Google OAuth connection for this tenant. Permission sync will
                  stop working until you reconnect.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDisconnectConfirm}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Disconnect
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

function SyncLogStatusCell({ status }: { status: string }) {
  if (status === "success") return <span className="text-success">{status}</span>;
  if (status === "failed") return <span className="text-destructive">{status}</span>;
  return <span className="text-muted-foreground">{status}</span>;
}

const STATUS_FILTERS: { value: SyncLogStatusFilter; label: string }[] = [
  { value: "all", label: "All activity" },
  { value: "failed", label: "Failures only" },
  { value: "skipped", label: "Skipped only" },
  { value: "success", label: "Successes only" },
];

/** Absolute date under the relative one — "3 days ago" cannot be correlated with anything. */
function SyncLogTimeCell({ createdAt }: { createdAt: string }) {
  return (
    <>
      <div>{formatRelativeTime(createdAt)}</div>
      <div className="text-xs">{new Date(createdAt).toLocaleString()}</div>
    </>
  );
}

function SyncLogTable({ tenantId }: { tenantId: string }) {
  const [statusFilter, setStatusFilter] = useState<SyncLogStatusFilter>("all");
  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useGoogleSyncLog(tenantId, statusFilter);
  const logs = data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-base font-semibold">Recent Sync Activity</h3>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as SyncLogStatusFilter)}
        >
          <SelectTrigger className="w-44" aria-label="Filter sync activity by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading sync activity…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          Could not load sync activity. {error instanceof Error ? error.message : ""}
        </p>
      ) : logs.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {statusFilter === "all"
            ? "No sync activity yet."
            : "No sync activity matching this filter."}
        </p>
      ) : (
        <div className="overflow-auto max-h-[36rem]">
          <table className="w-full text-sm border border-border rounded">
            <thead>
              <tr className="border-b border-border bg-muted text-muted-foreground text-left">
                <th className="px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">User</th>
                <th className="px-3 py-2 font-medium">Dashboard</th>
                <th className="px-3 py-2 font-medium">Action</th>
                <th className="px-3 py-2 font-medium">Provider</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                    <SyncLogTimeCell createdAt={log.createdAt} />
                  </td>
                  <td className="px-3 py-2">{log.userEmail}</td>
                  <td className="px-3 py-2">{log.dashboardTitle}</td>
                  <td className="px-3 py-2">{log.action}</td>
                  <td className="px-3 py-2">{log.provider}</td>
                  <td className="px-3 py-2">
                    <SyncLogStatusCell status={log.status} />
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{log.detail ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hasNextPage && (
        <div className="mt-3 flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
          <span className="text-xs text-muted-foreground">{logs.length} shown</span>
        </div>
      )}
    </div>
  );
}

// ─── Main section ─────────────────────────────────────────────────────────────

export function GoogleConnectionSection({ tenantId }: { tenantId: string }) {
  const { data, isLoading } = useGoogleConnection(tenantId);

  if (isLoading || !data) return null;

  return (
    <section aria-labelledby="google-connection-heading">
      <h2 id="google-connection-heading" className="text-lg font-semibold mb-4">
        Google Connection
      </h2>
      <div className="space-y-2">
        <div>
          <ConnectionStatusBadge status={data.status} />
          {data.connected && data.email && (
            <p className="text-sm text-muted-foreground mt-1">
              Connected as {data.email}
              {data.connectedAt && (
                <span className="ml-2">· {formatRelativeTime(data.connectedAt)}</span>
              )}
            </p>
          )}
        </div>
        <ConnectionActions tenantId={tenantId} status={data.status} connected={data.connected} />
        <SyncLogTable tenantId={tenantId} />
      </div>
    </section>
  );
}
